export const operatorTraversalPathParamName = "operatorTraversalPath";
export const operatorTraversalDepthParamName = "operatorTraversalDepth";

/**
 * Tool JSON Schema descriptions for `operatorTraversalPath` / `operatorTraversalDepth`.
 * Shared by Gemini, OpenAI Responses, and future providers that inject these parameters.
 */
export const operatorTraversalPathParamSchemaDescription =
  'Optional dot/bracket path to extract a specific value from the tool output (e.g. "steps[0].messaging_actions[0].email_body"). Use dot-separated keys and numeric bracket indices only — wildcards are not supported (no *, **, [*], ?, or JSONPath-style filters). When provided, only the resolved slice is returned.';

export const operatorTraversalDepthParamSchemaDescription =
  "Optional maximum nesting depth to return. Trims the output tree beyond this depth. Use a small value (e.g. 2 or 3) to get an overview of the structure, then drill in with operatorTraversalPath.";

/**
 * When traversal params are `anyOf` with null and listed in `required` (OpenAI pattern), tell the model to pass null when unused.
 */
export const operatorTraversalParamNullableJsonSchemaHint = " Pass null if not needed.";

/** A traversal path only takes effect when it is a non-empty string; `useToolCaller` skips traversal otherwise. */
export const isTraversalPathRequested = (path: unknown): path is string => typeof path === "string" && path !== "";

/** A traversal depth only takes effect when it is positive; `useToolCaller` skips trimming otherwise. */
export const isTraversalDepthRequested = (depth: unknown): depth is number =>
  typeof depth === "number" && depth > 0;

/**
 * Whether the model asked for a slice (`operatorTraversalPath`) or a depth-trimmed view
 * (`operatorTraversalDepth`) of a tool's output that will actually be applied. A renderer
 * written for the callback's full output shape should fall back to the generic tool card
 * when this is true.
 */
export const hasRequestedTraversal = (args: Record<string, unknown> | undefined): boolean =>
  isTraversalPathRequested(args?.[operatorTraversalPathParamName]) ||
  isTraversalDepthRequested(args?.[operatorTraversalDepthParamName]);

/**
 * Drops the traversal params unless they will take effect. The strict OpenAI schema lists
 * both as required-nullable, so every call to a traversable tool carries them; only a
 * non-empty path or a positive depth means the model used them.
 */
export const omitUnusedTraversalParams = <T extends Record<string, unknown>>(args: T): T => {
  const result: Record<string, unknown> = { ...args };
  if (!isTraversalPathRequested(result[operatorTraversalPathParamName])) {
    delete result[operatorTraversalPathParamName];
  }
  if (!isTraversalDepthRequested(result[operatorTraversalDepthParamName])) {
    delete result[operatorTraversalDepthParamName];
  }
  return result as T;
};

/**
 * Resolve a dot/bracket path against a JSON value.
 * Path syntax: `steps[0].messaging_actions[0].email_body` (keys and bracket indices).
 * Returns the resolved value or undefined if the path is invalid.
 */
export const resolveJsonPath = (obj: unknown, path: string): { value: unknown } | { error: string } => {
  const segments = parsePath(path);
  if (segments === null) {
    return { error: `Invalid path syntax: ${path}` };
  }

  let current: unknown = obj;
  for (const segment of segments) {
    if (current === null || current === undefined) {
      return { error: `Path not found: ${path} (reached null/undefined before resolving full path)` };
    }
    if (typeof segment === "number") {
      if (!Array.isArray(current)) {
        return { error: `Path not found: ${path} (expected array at index ${segment})` };
      }
      if (segment < 0 || segment >= current.length) {
        return { error: `Path not found: ${path} (index ${segment} out of bounds, length ${current.length})` };
      }
      current = current[segment];
    } else {
      if (typeof current !== "object") {
        return { error: `Path not found: ${path} (expected object at key "${segment}")` };
      }
      current = (current as Record<string, unknown>)[segment];
    }
  }

  return { value: current };
};

/**
 * Trim a JSON tree to a given depth. At the depth boundary, objects and arrays
 * are replaced with a summary placeholder. Primitives at or above the depth
 * limit are kept as-is.
 *
 * @param depth Maximum nesting levels to retain (1 = only top-level keys).
 */
export const trimToDepth = (obj: unknown, depth: number): unknown => {
  if (depth < 0) {
    return obj;
  }
  return depthWalk(obj, depth, 0);
};

const depthWalk = (value: unknown, maxDepth: number, currentDepth: number): unknown => {
  if (value === null || value === undefined || typeof value !== "object") {
    return value;
  }

  if (currentDepth >= maxDepth) {
    if (Array.isArray(value)) {
      return `[Array with ${value.length} items. Increase ${operatorTraversalDepthParamName} or use ${operatorTraversalPathParamName} to access.]`;
    }
    const keys = Object.keys(value);
    return `[Object with ${keys.length} keys: ${keys.slice(0, 5).join(", ")}${keys.length > 5 ? ", ..." : ""}. Increase ${operatorTraversalDepthParamName} or use ${operatorTraversalPathParamName} to access.]`;
  }

  if (Array.isArray(value)) {
    return value.map((item) => depthWalk(item, maxDepth, currentDepth + 1));
  }

  const result: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value)) {
    result[key] = depthWalk(val, maxDepth, currentDepth + 1);
  }
  return result;
};

/**
 * Parse a path string like "steps[0].messaging_actions[0].email_body"
 * into an array of string keys and numeric indices.
 * An empty string means "the root object".
 * Returns null if the path is syntactically invalid.
 */
export const parsePath = (path: string): Array<string | number> | null => {
  if (path === "") {
    return [];
  }

  const segments: Array<string | number> = [];
  let i = 0;

  // First segment can start without a leading dot
  if (path[i] !== "." && path[i] !== "[") {
    const start = i;
    while (i < path.length && path[i] !== "." && path[i] !== "[") {
      i++;
    }
    segments.push(path.slice(start, i));
  }

  while (i < path.length) {
    if (path[i] === ".") {
      i++;
      const start = i;
      while (i < path.length && path[i] !== "." && path[i] !== "[") {
        i++;
      }
      if (i === start) {
        return null;
      }
      segments.push(path.slice(start, i));
    } else if (path[i] === "[") {
      i++;
      if (i >= path.length) {
        return null;
      }
      const quote = path[i] === "'" || path[i] === '"' ? path[i] : null;
      if (quote) {
        i++;
        const start = i;
        while (i < path.length && path[i] !== quote) {
          i++;
        }
        if (i === path.length || path[i + 1] !== "]") {
          return null;
        }
        segments.push(path.slice(start, i));
        i += 2; // skip quote + ']'
      } else {
        const start = i;
        while (i < path.length && path[i] !== "]") {
          i++;
        }
        if (i === path.length) {
          return null;
        }
        const indexStr = path.slice(start, i);
        const index = Number(indexStr);
        if (Number.isInteger(index) && index >= 0) {
          segments.push(index);
        } else {
          segments.push(indexStr);
        }
        i++; // skip ']'
      }
    } else {
      return null;
    }
  }

  return segments;
};
